<script setup lang="ts">
import {computed, inject, onMounted, ref} from "vue";
import AiReport from "@/components/startups/AiReport.vue";
import {useStore} from "vue2-helpers/vuex";
import fileHelper from "@/helpers/file";
import startupsService from "@/services/startups.service";
import TrackerReport from "@/views/profile/TrackerReport.vue";

const {canLoadAiReport} = defineProps(['canLoadAiReport']);
const store = useStore();

const id = inject('companyId');
const name = inject('companyName').value;
const aiReportFailed = ref(null);
const aiReportData = ref(null);
const aiReportRaw = ref(null);
const aiReportLoading = ref(false);
const aiReportDownloading = ref(false);
const showAiResponseModal = ref(false);
const trackerReports = ref([]);

const userCanTestReport = computed(() => {
    return store.state.user.email && ['maxmms3','switchpitch1ga','nastywork2024'].some(email => store.state.user.email.includes(email));
});

onMounted(() => {
    if (canLoadAiReport) {
        aiReportFailed.value = false;
        getAiReport();
    } else {
        aiReportFailed.value = true;
    }

    getTrackerReports();
});

async function getAiReport(reload = 0) {
    aiReportData.value = null;
    aiReportRaw.value = null;
    aiReportFailed.value = false;
    aiReportLoading.value = true;

    try {
        const {data} = await startupsService.getAiReport(id, reload);

        if (typeof data === 'string') {
            aiReportRaw.value = data;
            aiReportFailed.value = true;
        } else {
            aiReportData.value = data;
        }
    } finally{
        aiReportLoading.value = false;
    }
}

async function downloadAiReport() {
    aiReportDownloading.value = true;
    const {data} = await startupsService.downloadAiReport(id);

    fileHelper.download(data,`${name}.pdf`);
    aiReportDownloading.value = false;
}

async function getTrackerReports() {
    const {data} = await startupsService.getTrackerReports(id);
    trackerReports.value = data;
}
</script>

<template>
    <div>
        <a
            v-if="!aiReportLoading"
            class="link link--reload link--top"
            @click="getAiReport(1)"
        >
            Rerun Overview Report
        </a>

        <div class="main-startup__tabs__item">
            <header class="main-startup__tabs__header">
                <h4 class="heading4 heading4--tertiary">Startup Overview</h4>

                <template v-if="aiReportDownloading">
                    <span>Downloading...</span>
                </template>

                <a v-else-if="aiReportData" class="button-download" @click="downloadAiReport">
                    Download
                </a>

                <a v-if="aiReportRaw && userCanTestReport" @click="showAiResponseModal = true">
                    Show AI response
                </a>
            </header>

            <template v-if="aiReportLoading">
                <div class="main-startup__tabs__item__inner main-startup__no-report">
                    <h4 class="heading4 heading4--tertiary mb-5">Generating Report…</h4>
                    <span class="loader-origin"></span>
                </div>
            </template>

            <template v-else>
                <div v-if="aiReportFailed" class="main-startup__tabs__item__inner main-startup__no-report">
                    <h4 class="heading4 heading4--tertiary main-startup__no-report__title">Not Enough Data to Generate Report</h4>

                    <p>Want more information about this startup? <a href="#">Contact Our Analysts</a></p>
                </div>

                <AiReport
                    v-if="aiReportData"
                    :ai-report-data="aiReportData"
                    class="main-startup__tabs__item__inner"
                />
            </template>
        </div>

        <TrackerReport
            v-for="report in trackerReports"
            :key="report.id"
            :report="report"
        />

        <b-modal v-model="showAiResponseModal" hide-footer title="AI response">
            {{aiReportRaw}}
        </b-modal>
    </div>
</template>

<style scoped>

</style>
